import { Alert, Button, Checkbox, Input, Typography } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { getBackendUrl } from "../utils";

import {
  captureNetsuiteSetupToken,
  clearNetsuiteSetupToken,
} from "./setupSession";
const { Title, Paragraph } = Typography;
type Setup = { inbound_access_token: string; setup_url: string };

export default function NetsuiteEmbeddedSetup() {
  const [token] = useState(captureNetsuiteSetupToken);
  const [setup, setSetup] = useState<Setup | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    if (!token) {
      setError("Reconnect to NetSuite to start embedded setup.");
      return;
    }
    setLoading(true);
    setError("");
    axios
      .post<Setup>(
        `${getBackendUrl()}/netsuite/embedded/setup`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then(({ data }) => {
        if (active) setSetup(data);
      })
      .catch((e) => {
        if (active)
          setError(
            e.response?.data?.error_message ??
              "Could not load setup. Please try again.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, attempt]);

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(setup!.inbound_access_token);
      setCopied(true);
      setError("");
    } catch {
      setError("Copy failed. Reveal the key and copy it manually.");
    }
  };

  const finish = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${getBackendUrl()}/netsuite/embedded/setup`,
        { complete: true },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      clearNetsuiteSetupToken();
      setSetup(null);
      window.location.replace(data.completion_url);
    } catch (e: any) {
      setError(
        e.response?.data?.error_message ??
          "Could not finish setup. Please try again.",
      );
      setLoading(false);
    }
  };

  return (
    <main className="max-w-2xl mx-auto p-8">
      <Title level={2}>Finish connecting mtCharge+</Title>
      <Paragraph>
        AccountLink is connected. Complete these steps in the same NetSuite
        account.
      </Paragraph>
      {error && (
        <Alert className="mb-4" type="error" message={error} showIcon />
      )}
      {!setup && (
        <Button
          loading={loading}
          disabled={!token}
          onClick={() => setAttempt(attempt + 1)}
        >
          Retry setup
        </Button>
      )}
      {setup && (
        <>
          <Title level={4}>1. Copy your connection key</Title>
          <Paragraph>
            This key lets mtCharge+ connect securely to your integration. Keep
            it private.
          </Paragraph>
          <div data-private="true" className="mb-4">
            <Input.Password
              aria-label="Inbound access token"
              readOnly
              value={setup.inbound_access_token}
              autoComplete="off"
            />
            <Button className="mt-2" onClick={copyKey}>
              {copied ? "Copied" : "Copy key"}
            </Button>
          </div>
          <Title level={4}>2. Open mtCharge+ Setup</Title>
          <Paragraph>
            Open Setup as an Administrator. Paste the key into the inbound
            access key field, enter the payment provider settings supplied by
            your integration provider, then choose{" "}
            <strong>Save &amp; Test</strong>.
          </Paragraph>
          <a href={setup.setup_url} target="_blank" rel="noopener noreferrer">
            Open mtCharge+ Setup in NetSuite
          </a>
          <Title className="mt-6" level={4}>
            3. Confirm setup
          </Title>
          <Paragraph>
            Return here after mtCharge+ reports a successful connection test.
          </Paragraph>
          <Checkbox
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          >
            I saved the key and the connection test succeeded.
          </Checkbox>
          <div className="mt-6">
            <Button
              type="primary"
              loading={loading}
              disabled={!confirmed}
              onClick={finish}
            >
              Finish connecting
            </Button>
          </div>
        </>
      )}
    </main>
  );
}
